import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { RoleGuideBanner } from '../components/RoleGuideBanner';
import ProfessionalAdvisor from '../components/ProfessionalAdvisor';
import DealTracker from '../components/DealTracker';
import MarketTrends from '../components/MarketTrends';
import AgentChoicePanel from '../components/AgentChoicePanel';
import FindProfessionals from '../components/FindProfessionals';
import BuyerConsultations from '../components/BuyerConsultations';
import BuyerJourney from '../components/BuyerJourney';
import GettingStartedGuide from '../components/GettingStartedGuide';
import RecoveryDiscountBanner from '../components/RecoveryDiscountBanner';
import AffiliateWidget from '../components/AffiliateWidget';
import DashboardTabs from '../components/DashboardTabs';
import InviteTeaserCard from '../components/InviteTeaserCard';
import FindProfessionalPromo from '../components/FindProfessionalPromo';
import SellerPulseBoostPanel from '../components/SellerPulseBoostPanel';
import LocalProsPanel from '../components/LocalProsPanel';
import ComplianceBanner from '../components/ComplianceBanner';
import SubscriptionsAndFeesCard from '../components/SubscriptionsAndFeesCard';
import { useQueryTab } from '../hooks/useQueryTab';
import { Button } from '../components/ui/button';
import { Plus, Home, Eye, Calendar as CalendarIcon, Clock, MapPin, CheckCircle, XCircle, AlertCircle, Pencil, Trash2, Briefcase, FileText, Users, MessageSquare, ClipboardCheck, HelpCircle, Activity, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySettings } from '../utils/currency';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SellerDashboard = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [activeTab, setActiveTab] = useQueryTab('listings', ['listings', 'deals', 'consultations', 'journey', 'find-pros', 'professional', 'agent-choice']);

  // Detect if seller has rental listings → show landlord-friendly terminology
  const hasRentals = useMemo(() => listings.some(l => l.listing_type === 'rent'), [listings]);
  const hasSales = useMemo(() => listings.some(l => l.listing_type === 'sale' || !l.listing_type), [listings]);
  const isLandlord = hasRentals && !hasSales;

  const sellerTabs = useMemo(() => [
    { id: 'listings', label: isLandlord ? 'My Rental Properties' : 'My Listings', shortLabel: 'Listings', icon: <Home className="h-4 w-4" />, testId: 'tab-listings' },
    { id: 'deals', label: isLandlord ? 'My Tenancies' : 'My Deals', shortLabel: isLandlord ? 'Tenancies' : 'Deals', icon: <FileText className="h-4 w-4" />, testId: 'tab-deals' },
    { id: 'consultations', label: 'Messages & Advice', shortLabel: 'Messages', icon: <MessageSquare className="h-4 w-4" />, testId: 'tab-consultations-seller' },
    { id: 'journey', label: isLandlord ? 'Landlord Guide' : 'Step-by-Step Guide', shortLabel: 'Guide', icon: <ClipboardCheck className="h-4 w-4" />, testId: 'tab-journey' },
    { id: 'find-pros', label: isLandlord ? 'Find a Letting Agent' : 'Find a Solicitor', shortLabel: isLandlord ? 'Agents' : 'Solicitors', icon: <Briefcase className="h-4 w-4" />, testId: 'tab-find-pros' },
  ], [isLandlord]);


  const fetchData = useCallback(async () => {
    try {
      const [listingsRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/properties/my-listings`),
        axios.get(`${API_URL}/api/bookings/seller`)
      ]);
      setListings(listingsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/bookings/${bookingId}/status?status=${status}`, {});
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const deleteListing = async (e, propertyId, title) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(propertyId);
    try {
      await axios.delete(`${API_URL}/api/properties/${propertyId}`);
      toast.success('Property deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete property');
    } finally {
      setDeleting(null);
    }
  };

  const { formatPrice } = useCurrencySettings();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] premium-theme">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="seller-dashboard">
      <Header />
      <RoleGuideBanner currentSection="/seller" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* CLEAN HEADER — seller's listings first, upsells in a drawer below.
            Same simplification pattern as BuyerDashboard. */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div>
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium">
              {isLandlord ? 'Landlord Portal' : 'Seller Portal'}
            </span>
            <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl text-[#1A1A1A]">
              {isLandlord ? 'My Rental Properties' : 'My Listings'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. List free — pay only when a verified buyer matches.
            </p>
          </div>
          <Link to="/seller/create">
            <Button
              className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm px-8 py-6 font-semibold shadow-lg shadow-[#E07A3A]/20 hover:shadow-xl hover:shadow-[#E07A3A]/30 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="create-listing-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              {isLandlord ? 'LIST RENTAL' : 'LIST PROPERTY'}
            </Button>
          </Link>
        </div>

        {/* Tabs */}
        <div className="mt-6">
          <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={sellerTabs} />
        </div>

        {/* Recovery banner auto-hides if no abandoned cart — safe above the fold. */}
        <RecoveryDiscountBanner dataTestId="seller-recovery-banner" />

        {activeTab === 'professional' && <ProfessionalAdvisor userRole="seller" />}

        {activeTab === 'agent-choice' && (
          <AgentChoicePanel
            userRole="seller"
            onChooseAgent={() => setActiveTab('find-pros')}
            onChooseWithout={() => setActiveTab('journey')}
          />
        )}

        {activeTab === 'journey' && <BuyerJourney userRole={isLandlord ? 'landlord' : 'seller'} />}

        {activeTab === 'find-pros' && <FindProfessionals userRole="seller" />}

        {activeTab === 'consultations' && <BuyerConsultations />}

        {activeTab === 'deals' && (
          <div className="space-y-6">
            <DealTracker userRole="seller" />
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-4">Market Pricing Trends</h3>
              <MarketTrends />
            </div>
          </div>
        )}

        {activeTab === 'listings' && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{listings.length}</p>
            <p className="text-slate-500 text-sm mt-1">Total Listings</p>
          </div>
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">
              {listings.filter(l => l.status === 'active').length}
            </p>
            <p className="text-slate-500 text-sm mt-1">Active</p>
          </div>
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{bookings.length}</p>
            <p className="text-slate-500 text-sm mt-1">{isLandlord ? 'Viewing Requests' : 'Booking Requests'}</p>
          </div>
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">
              {listings.reduce((sum, l) => sum + (l.views || 0), 0)}
            </p>
            <p className="text-slate-500 text-sm mt-1">Total Views</p>
          </div>
        </div>

        {/* Listings */}
        <div className="mb-12">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">Properties</h2>
          {listings.length > 0 ? (
            <div className="space-y-4">
              {listings.map((listing) => (
                <React.Fragment key={listing.id}>
                <div
                  onClick={() => window.location.href = `/property/${listing.id}`}
                  className="luxury-card p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center cursor-pointer hover:shadow-md transition-shadow"
                  data-testid={`listing-${listing.id}`}
                >
                  <div className="w-20 h-20 bg-slate-100 overflow-hidden flex-shrink-0">
                    <img
                      src={listing.images?.[0] || (
                        listing.property_type === 'land'
                          ? 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&auto=format&fit=crop'
                          : listing.property_type === 'commercial'
                            ? 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=200&auto=format&fit=crop'
                            : 'https://images.unsplash.com/photo-1757262798620-c2cc40cfb440?w=200&auto=format&fit=crop'
                      )}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[#1A1A1A] font-medium text-lg truncate">{listing.title}</h3>
                    <div className="flex flex-wrap gap-3 mt-1 text-slate-500 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {listing.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" />
                        {listing.views || 0} views
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 flex flex-col items-end gap-2">
                    <p className="font-['Playfair_Display'] text-xl text-[#E07A3A]">{formatPrice(listing.price)}</p>
                    <span className={`px-3 py-1 text-xs tracking-wider uppercase font-medium ${
                      listing.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-500'
                    }`}>
                      {listing.status}
                    </span>
                    <div className="flex gap-2 mt-1">
                      <Link to={`/seller/edit/${listing.id}`} onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline" className="border-[#E07A3A]/30 text-[#E07A3A] hover:bg-[#FEF3EC] h-8 px-3" data-testid={`edit-listing-${listing.id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" />Edit
                        </Button>
                      </Link>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-500 hover:bg-red-50 h-8 px-3"
                        onClick={(e) => deleteListing(e, listing.id, listing.title)}
                        disabled={deleting === listing.id}
                        data-testid={`delete-listing-${listing.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />{deleting === listing.id ? '...' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                </div>
                {listing.status === 'active' && (
                  <SellerPulseBoostPanel listing={listing} />
                )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="luxury-card p-10 text-center">
              <Home className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No listings yet</p>
              <Link to="/seller/create">
                <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E]">Create Your First Listing</Button>
              </Link>
            </div>
          )}
        </div>

        {/* Bookings */}
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">Booking Requests</h2>
          {bookings.length > 0 ? (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking.id} className="luxury-card p-5" data-testid={`booking-${booking.id}`}>
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                    <div>
                      <p className="text-[#1A1A1A] font-medium">{booking.user_name}</p>
                      <p className="text-slate-500 text-sm">{booking.user_email}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-slate-500 text-sm">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-4 w-4" />
                          {booking.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {booking.time}
                        </span>
                      </div>
                      {booking.message && (
                        <p className="text-slate-600 text-sm mt-2 italic">"{booking.message}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {booking.status === 'pending' ? (
                        <>
                          <Button
                            onClick={(e) => { e.preventDefault(); updateBookingStatus(booking.id, 'confirmed'); }}
                            className="bg-green-600 text-white hover:bg-green-700"
                            size="sm"
                            data-testid={`confirm-booking-${booking.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Confirm
                          </Button>
                          <Button
                            onClick={(e) => { e.preventDefault(); updateBookingStatus(booking.id, 'cancelled'); }}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            size="sm"
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Decline
                          </Button>
                        </>
                      ) : (
                        <span className={`px-4 py-2 text-xs tracking-wider uppercase font-medium ${
                          booking.status === 'confirmed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {booking.status}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="luxury-card p-10 text-center">
              <CalendarIcon className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No booking requests yet</p>
            </div>
          )}
        </div>
        </>
        )}

        {/* Help & Guide — collapsible, at bottom */}
        <details className="mt-8" data-testid="seller-help-guide">
          <summary className="cursor-pointer flex items-center gap-2 text-sm text-[#E07A3A] font-semibold tracking-wider uppercase hover:text-[#C4652E] transition-colors py-3">
            <HelpCircle className="h-4 w-4" />
            Help & Getting Started Guide
          </summary>
          <div className="mt-3">
            <GettingStartedGuide role="seller" />
          </div>
        </details>

        {/* ─── TOOLS & OFFERS DRAWER — relocated upsells.
              Default open so nothing is hidden from existing users, just
              moved below the user's actual listings. ─── */}
        <details
          className="mt-10 border border-slate-200 rounded-lg bg-white overflow-hidden"
          open
          data-testid="seller-tools-offers"
        >
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-[#E07A3A]" />
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E07A3A]">Tools &amp; Offers</p>
                <p className="text-sm font-semibold text-[#0F172A]">Pulse Report, pros, fees &amp; earnings · click to collapse</p>
              </div>
            </div>
            <span className="text-[10px] text-slate-500 hidden sm:block">All optional</span>
          </summary>

          <div className="p-5 space-y-5">
            <Link
              to={`/free-valuation?country=${(user?.country || 'UK').toUpperCase()}&ref=seller-dashboard`}
              className="flex flex-wrap items-center gap-4 bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] text-[#0F172A] px-5 py-4 border border-[#E07A3A]/40 hover:border-[#E07A3A] transition-colors group shadow-sm rounded"
              data-testid="seller-pulse-sell-cta"
            >
              <div className="w-10 h-10 bg-[#E07A3A]/10 border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
                <Activity className="h-5 w-5 text-[#E07A3A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">
                  Price With Confidence · £49 / ₹4,900 / C$79
                </p>
                <p className="text-sm font-semibold text-[#0F172A] truncate">
                  Independent Pulse Investment Report — median price, comps &amp; AI commentary in 60 seconds
                </p>
              </div>
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-[#E07A3A] px-3 py-1.5 group-hover:bg-[#C4652E] transition-colors flex-shrink-0" data-testid="seller-pulse-sell-cta-buy-btn">
                Order Report
              </span>
              <ArrowRight className="h-4 w-4 text-[#E07A3A] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>

            <FindProfessionalPromo compact country={user?.country || 'UK'} dataTestId="seller-find-pro-promo" />

            <LocalProsPanel
              role="seller"
              city={user?.city || ''}
              country={(user?.country || 'UK').toUpperCase()}
              dataTestId="seller-local-pros-panel"
            />

            <SubscriptionsAndFeesCard country={(user?.country || 'UK').toUpperCase()} role={isLandlord ? 'landlord' : 'seller'} dataTestId="seller-fees-card" />

            <ComplianceBanner />

            <InviteTeaserCard />

            <AffiliateWidget dataTestId="seller-affiliate-widget" />
          </div>
        </details>
      </div>
    </div>
  );
};

export default SellerDashboard;
